import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { dialogManager } from '../stores/DialogManager';
import { LinkExpires } from '../submodules/xbox/egr-gui-elements/LinkExpires';
import { layoutInformation } from '../submodules/xbox/utils/Layout';
import { getDialogShareUrl } from '../utils/dialogUtils';

import Dialog from '@egr/xbox/egr-gui-dialogs/utils/DialogOverride';
import { DialogContent } from '@egr/xbox/egr-gui-dialogs/whiteDialog/DialogContent';
import { DialogTitle } from '@egr/xbox/egr-gui-dialogs/whiteDialog/DialogTitle';
import { DialogTransition } from '@egr/xbox/egr-gui-elements/single-components/DialogTransition';
import { makeStyles } from '@egr/xbox/utils/tss-react';

const useEmbedDialogStyles = makeStyles({ name: 'EmbedDialog' })((theme) => ({
    content: {
        display: 'flex',
        flexDirection: 'row',
        padding: theme.spacing(3),
    },
    contentModal: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
    },
    contentFullscreen: {
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
    },
    columnBox: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    checkboxLabel: {
        alignSelf: 'flex-start',
        marginBottom: '-12px',
        paddingTop: '8px',
    },
    accordionBox: {
        marginTop: '4px',
    },
    embedCode: {
        paddingTop: '8px',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
    },
}));

export const EmbedDialog = () => {
    const { t } = useTranslation();
    const { classes, cx } = useEmbedDialogStyles();
    const { smallLayout } = layoutInformation.useState(['smallLayout']);
    const { embedDialog } = dialogManager.useState(['embedDialog']);
    const [expanded, setExpanded] = React.useState(false);

    if (embedDialog == null) {
        return null;
    }
    const { title, url, expiryDate } = embedDialog;
    const shareUrl = getDialogShareUrl(url);

    const embedCode = `<iframe width="560" height="315" src="${shareUrl}" frameborder="0" allowfullscreen allowvr="yes" allow="xr-spatial-tracking"></iframe>`;

    function onClose(): void {
        if (embedDialog?.onClose) {
            embedDialog.onClose();
        }
    }
    function renderDialogActions() {
        return (
            <DialogActions>
                <Button
                    onClick={() => {
                        void dialogManager.copyTextToClipboard(embedCode);
                    }}
                >
                    {t('Copy code')}
                </Button>

                <Button
                    onClick={() => {
                        void dialogManager.copyTextToClipboard(shareUrl);
                    }}
                >
                    {t('Copy link')}
                </Button>
            </DialogActions>
        );
    }

    return (
        <Dialog
            onClose={onClose}
            open={true}
            TransitionComponent={DialogTransition}
            fullScreen={smallLayout}
        >
            <DialogTitle
                back={onClose}
                shouldClose
            >
                {title}
            </DialogTitle>

            <DialogContent
                dividers
                className={cx(classes.content, smallLayout ? classes.contentFullscreen : classes.contentModal)}
            >
                <Box className={classes.columnBox}>
                    <Typography>{t('Add this interactive 3D content to your website by copying and pasting the code below.')}</Typography>
                    {expiryDate && (
                        <Typography>
                            <LinkExpires expiryDate={expiryDate} />
                        </Typography>
                    )}
                    <Box className={classes.accordionBox}>
                        <Accordion
                            expanded={expanded}
                            onChange={() => setExpanded(!expanded)}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography>{expanded ? t('Hide code') : t('Show code')}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography
                                    component="pre"
                                    className={classes.embedCode}
                                >
                                    {embedCode}
                                </Typography>
                            </AccordionDetails>
                        </Accordion>
                    </Box>
                </Box>
            </DialogContent>
            {renderDialogActions()}
        </Dialog>
    );
};

EmbedDialog.displayName = 'EmbedDialog';
